import type { RunEvent } from "../../lib/productsApi";
import { cap } from "./text";

/** One tool call of a run, as the live view shows it: what the agent did and what came back. */
export interface RunStep {
  id: string;
  name: string;
  title: string;
  /** What came back, or null while the call is still running. */
  result: string | null;
  failed: boolean;
}

// The prototype's names for the tools (its TOOLN), for calls the live view has no better words for.
const TOOL_NAMES: Record<string, string> = {
  find_tool: "Find a tool",
  web_search: "Web search",
  read_page: "Read a page",
  list_lessons: "Lessons",
  call_catalog_tool: "Data sources",
  linkedin_search_posts: "LinkedIn post search",
  linkedin_profile: "LinkedIn profiles",
  modelmatch_lookup: "Sales records (Model Match)",
  read: "Read files",
};

const str = (v: unknown) => (typeof v === "string" ? v : "");

// Why: a run reads its skills and tools as files named by slug; the owner sees their names.
const NAMES: Record<string, string> = { "meta-ads": "Meta ads", "google-ads": "Google Ads", "linkedin-ads": "LinkedIn Ads" };
const itemName = (slug: string) => NAMES[slug] ?? slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, " ");

function readTitle(path: string): string {
  if (/(^|\/)MATERIAL\.md$/i.test(path)) return "Read the owner's notes";
  if (/(^|\/)product\.md$/i.test(path)) return "Read the product profile";
  const skill = path.match(/skills\/([^/]+)\/SKILL\.md$/i);
  return skill ? `Read ${itemName(skill[1])}` : `Read ${path}`;
}

/** The prototype's words for a call, as its real learn runs wrote them ("Searched: ...", "Read https://..."). */
function title(name: string, args: Record<string, unknown>): string {
  switch (name) {
    case "web_search":
      return `Searched: ${str(args.query)}`;
    case "read_page":
      return `Read ${str(args.url)}`;
    case "find_tool":
      return `Looked for a tool: ${str(args.job)}`;
    case "call_catalog_tool":
      return `Catalog: ${str(args.endpoint_id)}`;
    case "read":
      return readTitle(str(args.path));
    case "save_product":
      return "Saved the draft profile for you to confirm";
    case "confirm_identity":
      return "Asked which business is yours";
    default:
      return TOOL_NAMES[name] ?? cap(name.replace(/_/g, " "));
  }
}

function resultText(name: string, failed: boolean, preview: string): string {
  if (failed) return /not found|404/i.test(preview) ? "not found (404)" : "failed";
  if (name === "web_search") {
    const n = preview.match(/"count":\s*(\d+)/);
    if (n) return `${n[1]} result${n[1] === "1" ? "" : "s"}`;
  }
  if (name === "read_page" || name === "read") return "read";
  if (name === "save_product" || name === "confirm_identity") return "waiting for you";
  return "done";
}

/** Every tool call in a run's log, oldest first, each with its result once it arrives. */
export function runSteps(events: RunEvent[]): RunStep[] {
  const steps: RunStep[] = [];
  const byId = new Map<string, RunStep>();
  for (const e of events) {
    if (e.type === "tool.call") {
      const name = str(e.data.name);
      const args = (e.data.args && typeof e.data.args === "object" ? e.data.args : {}) as Record<string, unknown>;
      const step: RunStep = { id: str(e.data.id) || `s${e.seq}`, name, title: title(name, args), result: null, failed: false };
      steps.push(step);
      byId.set(step.id, step);
    } else if (e.type === "tool.result") {
      const step = byId.get(str(e.data.id));
      if (!step) continue;
      step.failed = e.data.isError === true;
      step.result = resultText(step.name, step.failed, str(e.data.preview));
    }
  }
  return steps;
}

/** The newest error the run reported, if any. */
export function lastError(events: RunEvent[]): string | null {
  for (let i = events.length - 1; i >= 0; i--) if (events[i].type === "error") return str(events[i].data.message) || null;
  return null;
}
